import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { SyntheticEvent } from 'react';
import type { NetworkGateway } from '../application/networkGateway';
import type {
  NetworkCollectionItem,
  NetworkCollectionKind,
  NetworkConnection,
  NetworkCursor,
  NetworkProfessional,
  NetworkProfessionalProfile,
  NetworkProperty,
} from '../domain/network';
import type { ConversationMessage } from '../domain/workspaces';
import { nearestSettledStory } from './networkScroll';
import styles from './NetworkScreen.module.css';

interface NetworkScreenProps {
  readonly gateway: NetworkGateway;
  readonly navigateToAccess?: () => void;
  readonly navigateToDashboard?: () => void;
}

type Surface = 'home' | 'collections' | 'red' | 'activity';
type HomeMode = 'properties' | 'agencies' | 'opportunities';
type ProfileSection = 'inventory' | 'agency' | 'blog';

const collectionLabels: Record<NetworkCollectionKind, string> = {
  following: 'Seguidos',
  followers: 'Seguidores',
  mutual: 'Seguidos mutuamente',
  seen: 'Vistos',
  scans: 'Scans',
  'feed-favorites': 'Favoritos del feed',
};

const visualPhotos = [
  'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1600&q=84',
  'https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?auto=format&fit=crop&w=1600&q=84',
  'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&w=1600&q=84',
  'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1600&q=84',
] as const;

function defaultNavigateToAccess(): void {
  window.location.replace('/');
}

function defaultNavigateToDashboard(): void {
  window.location.assign('/dashboard');
}

function photoAt(index: number): string {
  return visualPhotos[index % visualPhotos.length] ?? visualPhotos[0];
}

function initials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(-2)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function timeLabel(value: string): string {
  return new Date(value).toLocaleTimeString('es-MX', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatMoney(value: number, currency: string): string {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}

function professionalRelationLabel(
  professional: NetworkProfessional,
): string {
  if (professional.hasActiveMatch) return 'SEGUIDOS MUTUAMENTE';
  if (professional.outgoingMatchStatus === 'pending') return 'SIGUE A';
  if (professional.incomingMatchStatus === 'pending') return 'TE SIGUE';
  return 'EN RED';
}

function professionalMatchLabel(
  professional: NetworkProfessional,
): string {
  if (professional.hasActiveMatch) return 'SEGUIDOS MUTUAMENTE';
  if (professional.outgoingMatchStatus === 'pending') return 'SIGUE A';
  if (professional.incomingMatchStatus === 'pending') {
    return 'REGRESAR MATCH';
  }
  return 'HACER MATCH';
}

function propertyMatchLabel(property: NetworkProperty): string {
  if (property.hasActiveMatch) return 'SEGUIDOS MUTUAMENTE';
  if (property.outgoingMatchStatus === 'pending') return 'SIGUE A';
  if (property.incomingMatchStatus === 'pending') return 'REGRESAR MATCH';
  return 'HACER MATCH';
}

export function NetworkScreen({
  gateway,
  navigateToAccess = defaultNavigateToAccess,
  navigateToDashboard = defaultNavigateToDashboard,
}: NetworkScreenProps): React.JSX.Element {
  const [surface, setSurface] = useState<Surface>('home');
  const [mode, setMode] = useState<HomeMode>('properties');
  const [nearMe, setNearMe] = useState(false);
  const [queryDraft, setQueryDraft] = useState('');
  const [query, setQuery] = useState('');
  const [professionals, setProfessionals] = useState<
    readonly NetworkProfessional[]
  >([]);
  const [properties, setProperties] = useState<readonly NetworkProperty[]>([]);
  const [mutualFollows, setMutualFollows] = useState<
    readonly NetworkConnection[]
  >([]);
  const [collectionItems, setCollectionItems] = useState<
    readonly NetworkCollectionItem[]
  >([]);
  const [selectedCollection, setSelectedCollection] =
    useState<NetworkCollectionKind | null>(null);
  const [nextCursor, setNextCursor] = useState<NetworkCursor | null>(null);
  const [directoryTotal, setDirectoryTotal] = useState(0);
  const [customCollectionCount, setCustomCollectionCount] = useState(0);
  const [customCollectionItems, setCustomCollectionItems] = useState(0);
  const [counts, setCounts] = useState({
    customers: 0,
    owners: 0,
    activeMatches: 0,
    pendingOutgoing: 0,
    visibleDms: 0,
  });
  const [selectedConnectionId, setSelectedConnectionId] = useState<
    string | null
  >(null);
  const [messages, setMessages] = useState<readonly ConversationMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [directoryLoading, setDirectoryLoading] = useState(false);
  const [collectionLoading, setCollectionLoading] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [matchNotice, setMatchNotice] = useState<string | null>(null);
  const [profilePreview, setProfilePreview] =
    useState<NetworkProfessionalProfile | null>(null);
  const [profileSection, setProfileSection] =
    useState<ProfileSection>('agency');
  const [profileLoading, setProfileLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [settledIndex, setSettledIndex] = useState(0);
  const [settling, setSettling] = useState(false);
  const feedRef = useRef<HTMLDivElement | null>(null);
  const settleTimerRef = useRef<number | null>(null);

  const refreshNetwork = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      const [network, propertyFeed, collections, relationshipCounts, mutual] =
        await Promise.all([
          gateway.loadNetwork(
            {
              query,
              nearMe,
              seenOnly: false,
              cursor: null,
              limit: 48,
            },
            signal,
          ),
          gateway.loadPropertyFeed(
            {
              query,
              nearMe,
              offset: 0,
              limit: 18,
            },
            signal,
          ),
          gateway.loadCollections(signal),
          gateway.loadCounts(signal),
          gateway.loadMutualFollows(signal),
        ]);

      if (!network.ok) {
        setError(network.message);
        setLoading(false);
        return;
      }
      if (!propertyFeed.ok) {
        setError(propertyFeed.message);
        setLoading(false);
        return;
      }

      setProfessionals(
        network.page.professionals.filter(
          (professional) => !professional.isSelf,
        ),
      );
      setProperties(propertyFeed.page.properties);
      setNextCursor(network.page.nextCursor);
      setDirectoryTotal(network.page.totalCount);

      if (collections.ok) {
        setCustomCollectionCount(collections.collections.length);
        setCustomCollectionItems(
          collections.collections.reduce(
            (sum, collection) => sum + collection.itemCount,
            0,
          ),
        );
      }
      if (relationshipCounts.ok) setCounts(relationshipCounts.counts);
      if (mutual.ok) setMutualFollows(mutual.connections);

      setSettledIndex(0);
      setLoading(false);
      setError(null);
    },
    [gateway, nearMe, query],
  );

  useEffect(() => {
    const controller = new AbortController();
    void gateway
      .resolveCurrentIdentity(controller.signal)
      .then(async (identity) => {
        if (!identity.ok) {
          navigateToAccess();
          return;
        }
        await refreshNetwork(controller.signal);
      })
      .catch((cause: unknown) => {
        if (
          !controller.signal.aborted &&
          !(cause instanceof DOMException && cause.name === 'AbortError')
        ) {
          navigateToAccess();
        }
      });

    return () => controller.abort();
  }, [gateway, navigateToAccess, refreshNetwork]);

  useEffect(() => {
    return () => {
      if (settleTimerRef.current !== null) {
        window.clearTimeout(settleTimerRef.current);
      }
    };
  }, []);

  const agencyStories = useMemo(
    () => professionals.filter((professional) => !professional.isSelf),
    [professionals],
  );

  const activeProperty =
    mode === 'properties' ? (properties[settledIndex] ?? null) : null;
  const activeProfessional =
    mode === 'agencies' ? (agencyStories[settledIndex] ?? null) : null;
  const selectedConnection =
    mutualFollows.find(
      (connection) => connection.matchId === selectedConnectionId,
    ) ?? null;

  function selectMode(nextMode: HomeMode): void {
    setSettledIndex(0);
    setSettling(false);
    setMode(nextMode);
    const viewport = feedRef.current;
    if (viewport !== null) viewport.scrollTop = 0;
  }

  function applySearch(event: SyntheticEvent<HTMLFormElement>): void {
    event.preventDefault();
    setLoading(true);
    setQuery(queryDraft.trim());
    setSurface('home');
  }

  function handleFeedScroll(): void {
    const viewport = feedRef.current;
    if (viewport === null) return;

    setSettling(true);
    if (settleTimerRef.current !== null) {
      window.clearTimeout(settleTimerRef.current);
    }

    settleTimerRef.current = window.setTimeout(() => {
      const nodes = Array.from(
        viewport.querySelectorAll<HTMLElement>('[data-network-story]'),
      );
      const index = nearestSettledStory(
        nodes.map((node) => node.offsetTop - viewport.scrollTop),
        4,
      );
      if (index !== null) setSettledIndex(index);
      setSettling(false);
    }, 180);
  }

  async function openProfessionalProfile(
    professional: NetworkProfessional,
  ): Promise<void> {
    setProfileLoading(true);
    setProfilePreview(null);
    setProfileSection('agency');

    const result = await gateway.loadProfessionalProfile(
      professional.agentId,
    );
    setProfileLoading(false);

    if (!result.ok) {
      setError(result.message);
      return;
    }

    setProfilePreview(result.profile);
    setError(null);
  }

  async function requestProfessionalMatch(
    professional: NetworkProfessional,
  ): Promise<void> {
    const result = await gateway.requestMatch(professional.agentId);
    if (!result.ok) {
      setError(result.message);
      return;
    }

    if (result.status === 'matched') {
      setMatchNotice(
        professional.publicName +
          ' regresó el Match. Ahora se siguen mutuamente y pueden conversar desde Colecciones.',
      );
      setProfessionals((current) =>
        current.map((candidate) =>
          candidate.agentId === professional.agentId
            ? {
                ...candidate,
                hasActiveMatch: true,
                outgoingMatchStatus: 'accepted',
                incomingMatchStatus: 'accepted',
              }
            : candidate,
        ),
      );
      const [mutual, relationshipCounts] = await Promise.all([
        gateway.loadMutualFollows(),
        gateway.loadCounts(),
      ]);
      if (mutual.ok) setMutualFollows(mutual.connections);
      if (relationshipCounts.ok) setCounts(relationshipCounts.counts);
      return;
    }

    setProfessionals((current) =>
      current.map((candidate) =>
        candidate.agentId === professional.agentId
          ? { ...candidate, outgoingMatchStatus: 'pending' }
          : candidate,
      ),
    );
    setCounts((current) => ({
      ...current,
      pendingOutgoing: current.pendingOutgoing + 1,
    }));
    setMatchNotice(
      'Match enviado. ' +
        professional.publicName +
        ' puede ignorarlo o regresarlo cuando lo decida.',
    );
  }

  async function requestPropertyMatch(
    property: NetworkProperty,
  ): Promise<void> {
    if (property.primaryAgentId === null) {
      setError('Esta propiedad todavía no tiene un profesional contactable.');
      return;
    }

    const result = await gateway.requestMatch(property.primaryAgentId);
    if (!result.ok) {
      setError(result.message);
      return;
    }

    setProperties((current) =>
      current.map((candidate) =>
        candidate.listingId === property.listingId
          ? {
              ...candidate,
              hasActiveMatch: result.status === 'matched',
              outgoingMatchStatus:
                result.status === 'matched' ? 'accepted' : 'pending',
              incomingMatchStatus:
                result.status === 'matched'
                  ? 'accepted'
                  : candidate.incomingMatchStatus,
            }
          : candidate,
      ),
    );

    setMatchNotice(
      result.status === 'matched'
        ? 'Match recíproco. La relación queda disponible en Seguidos mutuamente.'
        : 'Match enviado al profesional responsable. El contacto sigue cerrado hasta que lo regrese.',
    );

    const [mutual, relationshipCounts] = await Promise.all([
      gateway.loadMutualFollows(),
      gateway.loadCounts(),
    ]);
    if (mutual.ok) setMutualFollows(mutual.connections);
    if (relationshipCounts.ok) setCounts(relationshipCounts.counts);
  }

  async function toggleFeedFavorite(
    property: NetworkProperty,
  ): Promise<void> {
    const next = !property.isFavorite;
    const result = await gateway.setFeedFavorite(property.listingId, next);
    if (!result.ok) {
      setError(result.message);
      return;
    }

    setProperties((current) =>
      current.map((candidate) =>
        candidate.listingId === property.listingId
          ? { ...candidate, isFavorite: next }
          : candidate,
      ),
    );
    setError(null);
  }

  async function loadCollection(
    kind: NetworkCollectionKind,
  ): Promise<void> {
    setSelectedCollection(kind);
    setCollectionLoading(true);
    setMessages([]);
    setSelectedConnectionId(null);

    const result = await gateway.loadCollectionItems(kind);
    setCollectionLoading(false);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    setCollectionItems(result.items);
    setError(null);
  }

  async function loadMoreDirectory(): Promise<void> {
    if (nextCursor === null || directoryLoading) return;

    setDirectoryLoading(true);
    const result = await gateway.loadNetwork({
      query,
      nearMe,
      seenOnly: false,
      cursor: nextCursor,
      limit: 48,
    });
    setDirectoryLoading(false);

    if (!result.ok) {
      setError(result.message);
      return;
    }

    const additions = result.page.professionals.filter(
      (professional) => !professional.isSelf,
    );
    setProfessionals((current) => {
      const known = new Set(
        current.map((professional) => professional.agentId),
      );
      return [
        ...current,
        ...additions.filter(
          (professional) => !known.has(professional.agentId),
        ),
      ];
    });
    setNextCursor(result.page.nextCursor);
    setDirectoryTotal(result.page.totalCount);
    setError(null);
  }

  async function openConnection(
    connection: NetworkConnection,
  ): Promise<void> {
    setSelectedConnectionId(connection.matchId);
    setMessages([]);

    if (connection.conversationId === null) {
      setError(
        'La relación es mutua, pero su conversación todavía no está disponible.',
      );
      return;
    }

    setChatLoading(true);
    const result = await gateway.loadMessages(connection.conversationId);
    setChatLoading(false);

    if (!result.ok) {
      setError(result.message);
      return;
    }

    setMessages(result.messages);
    setError(null);
  }

  async function sendMessage(
    event: SyntheticEvent<HTMLFormElement>,
  ): Promise<void> {
    event.preventDefault();
    const body = draft.trim();
    if (
      body === '' ||
      selectedConnection?.conversationId === null ||
      selectedConnection?.conversationId === undefined
    ) {
      return;
    }

    setSending(true);
    const sent = await gateway.sendMessage(
      selectedConnection.conversationId,
      body,
    );
    if (!sent.ok) {
      setSending(false);
      setError(sent.message);
      return;
    }

    const refreshed = await gateway.loadMessages(
      selectedConnection.conversationId,
    );
    setSending(false);
    if (!refreshed.ok) {
      setError(refreshed.message);
      return;
    }

    setDraft('');
    setMessages(refreshed.messages);
    setError(null);
  }

  const actionDisabled = settling || loading;

  return (
    <main className={styles.shell}>
      <header className={styles.appBar}>
        <button
          className={styles.brand}
          type="button"
          onClick={navigateToDashboard}
        >
          <strong>Alpha•MLS</strong>
          <span>NETWORK</span>
        </button>
        <form className={styles.search} onSubmit={applySearch}>
          <label htmlFor="network-search">Buscar en Network</label>
          <input
            id="network-search"
            value={queryDraft}
            onChange={(event) => setQueryDraft(event.currentTarget.value)}
            placeholder="Nombre, ciudad, Agency o propiedad"
            autoComplete="off"
          />
          <button type="submit" aria-label="Buscar">⌕</button>
        </form>
        <button
          type="button"
          className={styles.menuButton}
          aria-label="Menú"
        >
          ⋮
        </button>
      </header>

      {surface === 'home' ? (
        <nav className={styles.navigationStack} aria-label="Network">
          <div className={styles.modeSwitch}>
            <button
              type="button"
              aria-pressed={mode === 'properties'}
              onClick={() => selectMode('properties')}
            >
              Propiedades
            </button>
            <button
              type="button"
              aria-pressed={mode === 'agencies'}
              onClick={() => selectMode('agencies')}
            >
              Agencias
            </button>
            <button
              type="button"
              aria-pressed={mode === 'opportunities'}
              onClick={() => selectMode('opportunities')}
            >
              Oportunidades
            </button>
          </div>
          <div className={styles.homeSubnav}>
            <button
              type="button"
              onClick={() => {
                setSelectedCollection(null);
                setSurface('collections');
              }}
            >
              Colecciones
            </button>
            <button
              type="button"
              aria-pressed={nearMe}
              onClick={() => {
                setLoading(true);
                setNearMe((current) => !current);
              }}
            >
              Cerca de ti
            </button>
          </div>
        </nav>
      ) : null}

      {error !== null ? (
        <div className={styles.error} role="alert">{error}</div>
      ) : null}
      {matchNotice !== null ? (
        <div className={styles.notice} role="status">
          <span>{matchNotice}</span>
          <button type="button" onClick={() => setMatchNotice(null)}>×</button>
        </div>
      ) : null}
      {loading ? (
        <div className={styles.loading} role="status">
          Preparando Network…
        </div>
      ) : null}

      {!loading && surface === 'home' && mode !== 'opportunities' ? (
        <section className={styles.homeShell}>
          <div
            className={styles.feedViewport}
            ref={feedRef}
            onScroll={handleFeedScroll}
          >
            {mode === 'properties'
              ? properties.map((property, index) => (
                  <article
                    className={styles.story}
                    data-network-story
                    key={property.listingId}
                    style={{
                      backgroundImage:
                        'linear-gradient(180deg,rgba(1,10,28,.18),rgba(1,10,28,.08) 42%,rgba(1,10,28,.82)),url(' +
                        photoAt(index) +
                        ')',
                    }}
                  >
                    <div className={styles.storyTopline}>
                      <span>
                        {property.transactionType === 'sale'
                          ? 'EN VENTA'
                          : 'EN RENTA'}
                      </span>
                      <small>{property.mediaCount} fotos</small>
                    </div>
                    <div className={styles.storyCopy}>
                      <p>{property.agencyName}</p>
                      <h1>{property.title}</h1>
                      <span>
                        {property.city}, {property.state}
                      </span>
                      <strong>
                        {formatMoney(property.price, property.currency)}
                      </strong>
                      <small>
                        {property.primaryAgentName ??
                          'Profesional responsable por asignar'}
                      </small>
                    </div>
                  </article>
                ))
              : agencyStories.map((professional, index) => (
                  <article
                    className={styles.story}
                    data-network-story
                    key={professional.agentId}
                    style={{
                      backgroundImage:
                        'linear-gradient(180deg,rgba(1,10,28,.2),rgba(1,10,28,.08) 35%,rgba(1,10,28,.86)),url(' +
                        photoAt(index + 1) +
                        ')',
                    }}
                  >
                    <div className={styles.storyTopline}>
                      <span>{professional.agencyName ?? 'Agency independiente'}</span>
                      <div className={styles.storyTopActions}>
                        <small>{professionalRelationLabel(professional)}</small>
                        <button
                          type="button"
                          className={styles.profilePeek}
                          aria-label={'Ver perfil de ' + professional.publicName}
                          onClick={() => void openProfessionalProfile(professional)}
                        >
                          ◉
                        </button>
                      </div>
                    </div>
                    <div className={styles.agencyPanel}>
                      <span className={styles.avatar}>
                        {initials(professional.publicName)}
                      </span>
                      <div>
                        <p>
                          {professional.professionalRole ?? 'Profesional'} ·{' '}
                          {professional.city}
                        </p>
                        <h1>{professional.publicName}</h1>
                        <span>
                          {professional.headline ??
                            professional.bio ??
                            'Profesional inmobiliario verificado dentro de Alpha•MLS.'}
                        </span>
                        <div className={styles.tags}>
                          {professional.specialties
                            .slice(0, 4)
                            .map((tag) => <b key={tag}>{tag}</b>)}
                        </div>
                      </div>
                    </div>
                  </article>
                ))}
          </div>

          <div
            className={
              settling
                ? styles.scrollActionBarSettling
                : styles.scrollActionBar
            }
            aria-busy={settling}
          >
            {mode === 'properties' && activeProperty !== null ? (
              <>
                <button
                  type="button"
                  disabled={
                    actionDisabled ||
                    activeProperty.primaryAgentId === null ||
                    activeProperty.outgoingMatchStatus === 'pending' ||
                    activeProperty.hasActiveMatch
                  }
                  onClick={() => void requestPropertyMatch(activeProperty)}
                >
                  <span>♡</span>
                  <b>{propertyMatchLabel(activeProperty)}</b>
                </button>
                <button
                  type="button"
                  disabled={actionDisabled || !activeProperty.hasActiveMatch}
                  onClick={() => {
                    setSelectedCollection('mutual');
                    setSurface('collections');
                    void loadCollection('mutual');
                  }}
                >
                  <span>◌</span>
                  <b>MENSAJE</b>
                </button>
                <button
                  type="button"
                  disabled={actionDisabled}
                  onClick={() => void toggleFeedFavorite(activeProperty)}
                >
                  <span>{activeProperty.isFavorite ? '★' : '☆'}</span>
                  <b>FAVORITOS</b>
                </button>
              </>
            ) : null}

            {mode === 'agencies' && activeProfessional !== null ? (
              <>
                <button
                  type="button"
                  disabled={
                    actionDisabled ||
                    !activeProfessional.acceptsMatches ||
                    activeProfessional.outgoingMatchStatus === 'pending' ||
                    activeProfessional.hasActiveMatch
                  }
                  onClick={() =>
                    void requestProfessionalMatch(activeProfessional)
                  }
                >
                  <span>♡</span>
                  <b>{professionalMatchLabel(activeProfessional)}</b>
                </button>
                <button
                  type="button"
                  disabled={actionDisabled || !activeProfessional.hasActiveMatch}
                  onClick={() => {
                    setSelectedCollection('mutual');
                    setSurface('collections');
                    void loadCollection('mutual');
                  }}
                >
                  <span>◌</span>
                  <b>MENSAJE</b>
                </button>
                <button
                  type="button"
                  disabled
                  title="Disponible cuando se implemente la selección de inventario a compartir"
                >
                  <span>↗</span>
                  <b>COMPARTIR INVENTARIO</b>
                </button>
              </>
            ) : null}
          </div>
        </section>
      ) : null}

      {!loading && surface === 'home' && mode === 'opportunities' ? (
        <section className={styles.opportunities}>
          <p>OPORTUNIDADES</p>
          <h1>Relaciones que pueden convertirse en negocio.</h1>
          <div className={styles.opportunityGrid}>
            <article>
              <span>WORKSHOPS</span>
              <h2>Sesiones especializadas</h2>
              <p>
                Superficie reservada para experiencias verificadas de la
                comunidad. Aún no existe agenda publicada.
              </p>
              <button type="button" disabled>Sin agenda activa</button>
            </article>
            <article>
              <span>BOOT CAMPS</span>
              <h2>Entrenamiento inmobiliario</h2>
              <p>
                El acceso se habilitará cuando exista programación persistida y
                control de entitlement.
              </p>
              <button type="button" disabled>Sin agenda activa</button>
            </article>
            <article>
              <span>SIGNATURE</span>
              <h2>Oportunidades de alta especialidad</h2>
              <p>
                Espacio preparado para relaciones de alto valor sin inventar
                oferta ni disponibilidad.
              </p>
              <button type="button" disabled>Sin oportunidades publicadas</button>
            </article>
          </div>
        </section>
      ) : null}

      {!loading && surface === 'collections' ? (
        <section className={styles.collectionsPage}>
          <header className={styles.collectionsHero}>
            <div>
              <p>
                {selectedCollection === null
                  ? 'MIS COLECCIONES'
                  : collectionLabels[selectedCollection].toUpperCase()}
              </p>
              <h1>
                {selectedCollection === null
                  ? 'Explora · conecta · invierte'
                  : collectionLabels[selectedCollection]}
              </h1>
              <span>
                {selectedCollection === null
                  ? `${String(customCollectionCount)} colecciones personales · ${String(customCollectionItems)} propiedades guardadas`
                  : 'Relaciones y activos asociados a este conjunto.'}
              </span>
            </div>
            {selectedCollection !== null ? (
              <button
                type="button"
                onClick={() => {
                  setSelectedCollection(null);
                  setCollectionItems([]);
                }}
              >
                Volver
              </button>
            ) : null}
          </header>

          <nav className={styles.collectionChips} aria-label="Colecciones">
            {(Object.keys(collectionLabels) as NetworkCollectionKind[]).map(
              (kind) => (
                <button
                  type="button"
                  key={kind}
                  aria-pressed={selectedCollection === kind}
                  onClick={() => void loadCollection(kind)}
                >
                  {collectionLabels[kind]}
                </button>
              ),
            )}
          </nav>

          {collectionLoading ? (
            <div className={styles.loadingInline} role="status">
              Abriendo colección…
            </div>
          ) : null}

          {selectedCollection === null ? (
            <div className={styles.collectionOverview}>
              <article>
                <strong>Seguidos</strong>
                <span>{counts.pendingOutgoing}</span>
                <small>Matches unilaterales enviados.</small>
              </article>
              <article>
                <strong>Seguidos mutuamente</strong>
                <span>{counts.activeMatches}</span>
                <small>Relaciones recíprocas contactables.</small>
              </article>
              <article>
                <strong>Favoritos del feed</strong>
                <span>★</span>
                <small>Propiedades marcadas desde Network.</small>
              </article>
              <article>
                <strong>Vistos y Scans</strong>
                <span>◎</span>
                <small>Huella de exploración profesional.</small>
              </article>
            </div>
          ) : null}

          {selectedCollection !== null &&
          selectedCollection !== 'mutual' &&
          !collectionLoading ? (
            <div className={styles.mixedList}>
              {collectionItems.map((item, index) => (
                <article key={item.itemType + ':' + item.itemId}>
                  <div
                    className={styles.mixedThumb}
                    style={{
                      backgroundImage:
                        'linear-gradient(180deg,rgba(3,12,29,.12),rgba(3,12,29,.72)),url(' +
                        photoAt(index) +
                        ')',
                    }}
                  >
                    <span>{item.itemType === 'professional' ? 'Agency' : 'Propiedad'}</span>
                  </div>
                  <div>
                    <b>{item.relationshipLabel}</b>
                    <h2>{item.title}</h2>
                    <p>{item.subtitle ?? item.agencyName ?? 'Alpha•MLS'}</p>
                    <small>
                      {[item.city, item.state].filter(Boolean).join(', ')}
                    </small>
                  </div>
                </article>
              ))}
              {collectionItems.length === 0 ? (
                <div className={styles.empty}>
                  <strong>Sin elementos todavía.</strong>
                  <span>
                    Este conjunto permanece vacío hasta que exista actividad
                    real que lo alimente.
                  </span>
                </div>
              ) : null}
            </div>
          ) : null}

          {selectedCollection === 'mutual' && !collectionLoading ? (
            <section className={styles.mutualLayout}>
              <aside className={styles.mutualList}>
                {mutualFollows.map((connection) => (
                  <button
                    type="button"
                    key={connection.matchId}
                    className={
                      selectedConnectionId === connection.matchId
                        ? styles.connectionActive
                        : styles.connection
                    }
                    onClick={() => void openConnection(connection)}
                  >
                    <span className={styles.avatar}>
                      {initials(connection.publicName)}
                    </span>
                    <span>
                      <strong>{connection.publicName}</strong>
                      <small>
                        {connection.agencyName ?? 'Agency independiente'} ·{' '}
                        {connection.city}
                      </small>
                    </span>
                    <b>Conversar</b>
                  </button>
                ))}
              </aside>

              <section className={styles.chat}>
                {selectedConnection === null ? (
                  <div className={styles.chatEmpty}>
                    <span>◎</span>
                    <h2>Selecciona un seguido mutuo</h2>
                    <p>
                      Sólo aquí aparece el canal de conversación habilitado por
                      reciprocidad.
                    </p>
                  </div>
                ) : (
                  <>
                    <header>
                      <span className={styles.avatar}>
                        {initials(selectedConnection.publicName)}
                      </span>
                      <div>
                        <strong>{selectedConnection.publicName}</strong>
                        <small>
                          {selectedConnection.agencyName ??
                            'Agency independiente'}
                        </small>
                      </div>
                      <b>Seguidos mutuamente</b>
                    </header>
                    <div className={styles.messages}>
                      {chatLoading ? <p>Abriendo conversación…</p> : null}
                      {messages.map((message) => (
                        <article
                          key={message.messageId}
                          className={
                            message.isMine
                              ? styles.messageMine
                              : styles.messageOther
                          }
                        >
                          {!message.isMine ? (
                            <strong>{message.senderName}</strong>
                          ) : null}
                          <p>{message.body}</p>
                          <time dateTime={message.createdAt}>
                            {timeLabel(message.createdAt)}
                          </time>
                        </article>
                      ))}
                    </div>
                    <form
                      className={styles.composer}
                      onSubmit={(event) => void sendMessage(event)}
                    >
                      <label htmlFor="network-message">Mensaje</label>
                      <textarea
                        id="network-message"
                        rows={1}
                        maxLength={4000}
                        value={draft}
                        onChange={(event) =>
                          setDraft(event.currentTarget.value)
                        }
                        placeholder="Mensaje"
                      />
                      <button
                        type="submit"
                        aria-label="Enviar"
                        disabled={sending || draft.trim() === ''}
                      >
                        {sending ? '…' : '➤'}
                      </button>
                    </form>
                  </>
                )}
              </section>
            </section>
          ) : null}
        </section>
      ) : null}

      {!loading && surface === 'red' ? (
        <section className={styles.directoryPage}>
          <header>
            <div>
              <p>RED MUNDIAL INMOBILIARIA</p>
              <h1>RED</h1>
              <span>
                Directorio visible de profesionales de Alpha•MLS. Puedes
                enviar Match; la visibilidad nunca concede contacto directo.
              </span>
            </div>
            <strong>{directoryTotal}</strong>
          </header>

          <div className={styles.directoryList}>
            {professionals.map((professional) => {
              const action = professionalMatchLabel(professional);
              const canRequest =
                professional.acceptsMatches &&
                !professional.hasActiveMatch &&
                professional.outgoingMatchStatus !== 'pending';

              return (
                <article key={professional.agentId}>
                  <span className={styles.avatar}>
                    {initials(professional.publicName)}
                  </span>
                  <div>
                    <strong>{professional.publicName}</strong>
                    <small>
                      {professional.agencyName ?? 'Agency independiente'} ·{' '}
                      {professional.city}
                    </small>
                    <em>
                      {professional.headline ??
                        professional.professionalRole ??
                        'Profesional inmobiliario'}
                    </em>
                  </div>
                  <b>{professionalRelationLabel(professional)}</b>
                  <div className={styles.directoryActions}>
                    <button
                      type="button"
                      aria-label={'Ver perfil de ' + professional.publicName}
                      onClick={() => void openProfessionalProfile(professional)}
                    >
                      Ver
                    </button>
                    <button
                      type="button"
                      disabled={!canRequest}
                      aria-label={action + ' a ' + professional.publicName}
                      onClick={() =>
                        void requestProfessionalMatch(professional)
                      }
                    >
                      {action}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>

          {nextCursor !== null ? (
            <button
              type="button"
              className={styles.loadMore}
              disabled={directoryLoading}
              onClick={() => void loadMoreDirectory()}
            >
              {directoryLoading ? 'Cargando…' : 'Cargar más perfiles'}
            </button>
          ) : null}
        </section>
      ) : null}

      {!loading && surface === 'activity' ? (
        <section className={styles.activityPage}>
          <p>ACTIVIDAD</p>
          <h1>Network</h1>
          <div>
            <article>
              <strong>{counts.pendingOutgoing}</strong>
              <span>Sigue a</span>
            </article>
            <article>
              <strong>{counts.activeMatches}</strong>
              <span>Seguidos mutuamente</span>
            </article>
            <article>
              <strong>{counts.visibleDms}</strong>
              <span>Conversaciones habilitadas</span>
            </article>
            <article>
              <strong>{counts.customers}</strong>
              <span>Customers</span>
            </article>
            <article>
              <strong>{counts.owners}</strong>
              <span>Owners</span>
            </article>
          </div>
        </section>
      ) : null}

      {profileLoading ? (
        <div className={styles.profileLoading} role="status">
          Abriendo perfil profesional…
        </div>
      ) : null}

      {profilePreview !== null ? (
        <section className={styles.profilePreview} aria-label="Perfil profesional">
          <div
            className={styles.profileCover}
            style={{
              backgroundImage:
                'linear-gradient(to top,rgba(4,17,32,.96),rgba(4,17,32,.18)),url(' +
                photoAt(2) +
                ')',
            }}
          >
            <button
              type="button"
              className={styles.profileClose}
              aria-label="Cerrar perfil"
              onClick={() => setProfilePreview(null)}
            >
              ←
            </button>
            <div className={styles.profileIdentity}>
              <span className={styles.avatar}>
                {initials(profilePreview.publicName)}
              </span>
              <div>
                <p>{profilePreview.agencyName ?? 'Agency independiente'}</p>
                <h1>{profilePreview.publicName}</h1>
                <span>
                  {profilePreview.headline ??
                    profilePreview.professionalRole ??
                    'Profesional inmobiliario'}
                </span>
              </div>
            </div>
          </div>

          <nav className={styles.profileTabs} aria-label="Perfil de Agency">
            <button
              type="button"
              aria-pressed={profileSection === 'inventory'}
              onClick={() => setProfileSection('inventory')}
            >
              Propiedades
            </button>
            <button
              type="button"
              aria-pressed={profileSection === 'agency'}
              onClick={() => setProfileSection('agency')}
            >
              Agency
            </button>
            <button
              type="button"
              aria-pressed={profileSection === 'blog'}
              onClick={() => setProfileSection('blog')}
            >
              Blog
            </button>
          </nav>

          {profileSection === 'agency' ? (
            <div className={styles.profileAgency}>
              <section>
                <p>PERFIL SOCIAL DE AGENCY</p>
                <h2>{profilePreview.agencyName ?? profilePreview.publicName}</h2>
                <span>
                  {profilePreview.bio ??
                    'Perfil profesional visible dentro de la red Alpha•MLS.'}
                </span>
                <dl>
                  <div>
                    <dt>Rol</dt>
                    <dd>
                      {profilePreview.professionalRole ?? 'Profesional'}
                    </dd>
                  </div>
                  <div>
                    <dt>Registro</dt>
                    <dd>
                      {profilePreview.licenseNumber ?? 'No publicado'}
                    </dd>
                  </div>
                  <div>
                    <dt>Ubicación</dt>
                    <dd>
                      {profilePreview.city}, {profilePreview.state}
                    </dd>
                  </div>
                </dl>
                <div className={styles.tags}>
                  {profilePreview.specialties.map((tag) => (
                    <b key={tag}>{tag}</b>
                  ))}
                </div>
              </section>
              <aside>
                <strong>Contacto protegido</strong>
                <p>
                  Ver este perfil no concede mensajería. El contacto directo
                  sigue condicionado al Match recíproco.
                </p>
              </aside>
            </div>
          ) : null}

          {profileSection === 'inventory' ? (
            <div className={styles.profileInventory}>
              {profilePreview.inventory.map((listing) => (
                <article key={listing.listingId}>
                  <small>
                    {listing.transactionType === 'sale' ? 'VENTA' : 'RENTA'}
                  </small>
                  <h2>{listing.title}</h2>
                  <strong>
                    {formatMoney(listing.price, listing.currency)}
                  </strong>
                  <span>
                    {listing.propertyType} · {listing.city}, {listing.state}
                  </span>
                </article>
              ))}
              {profilePreview.inventory.length === 0 ? (
                <div className={styles.empty}>
                  Sin inventario público representado en este momento.
                </div>
              ) : null}
            </div>
          ) : null}

          {profileSection === 'blog' ? (
            <div className={styles.profileBlog}>
              {profilePreview.blogPosts.map((post) => (
                <article key={post.postId}>
                  <small>BLOG · AGENCY</small>
                  <h2>{post.title}</h2>
                  {post.subtitle !== null ? (
                    <strong>{post.subtitle}</strong>
                  ) : null}
                  <p>{post.body}</p>
                </article>
              ))}
              {profilePreview.blogPosts.length === 0 ? (
                <div className={styles.empty}>
                  Esta Agency todavía no publicó entradas en su Blog.
                </div>
              ) : null}
            </div>
          ) : null}
        </section>
      ) : null}

      <nav className={styles.bottomNav} aria-label="Navegación principal Network">
        <button type="button" onClick={navigateToDashboard}>
          ◆<span>Dashboard</span>
        </button>
        <button
          type="button"
          className={surface === 'home' ? styles.bottomActive : undefined}
          onClick={() => setSurface('home')}
        >
          ⌂<span>Network</span>
        </button>
        <button
          type="button"
          className={surface === 'red' ? styles.bottomActive : undefined}
          onClick={() => setSurface('red')}
        >
          ◎<span>RED</span>
        </button>
        <button
          type="button"
          className={surface === 'activity' ? styles.bottomActive : undefined}
          onClick={() => setSurface('activity')}
        >
          ↗<span>Actividad</span>
        </button>
        <a href="/agency">
          ◔<span>Perfil</span>
        </a>
      </nav>
    </main>
  );
}
